import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppState } from '@/context/appState';
import {
  ReviewAPI,
  CreateReviewBody,
  JudgeMeReview,
} from '@/helpers/api/ReviewAPI';
import {
  ErrorNotification,
  SuccessNotification,
} from '@/components/toast/toastMessages';
import { Product } from '@/context/types';
import { ProductReviewsData } from '@/components/CustomerReviews/CustomerReviews';

type SortOption = 'latest' | 'highest' | 'lowest';

export default function useReviews(
  { id: productId, handle } = { id: '', handle: '' },
) {
  const dispatch = useAppDispatch();
  const { reviews, user } = useAppState();

  // Local state for ephemeral UI controls
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('latest');

  // Safely extract global state for the current product
  const productReviews = productId ? reviews.data[productId] : null;
  const data = productReviews?.list || [];
  const isLoading = productReviews?.loading || false;

  // 1. Core Fetch Logic (Handles both Initial Load & Pagination)
  const fetchReviews = useCallback(
    async (targetPage: number, reset: boolean = false) => {
      if (!productId) return;

      if (reset) {
        dispatch({ type: 'SET_REVIEWS_LOADING', productId, loading: true });
        setPage(1);
      } else {
        setIsFetchingMore(true);
      }

      try {
        const res: ProductReviewsData = await ReviewAPI.get({ handle });
        // Assume ReviewAPI.get is updated to accept sortBy

        // Determine if we've hit the end of the paginated list
        const fetchedCount = res.reviews.length;
        setHasMore(fetchedCount === 10); // If less than perPage, no more records

        // Inside useReviews.tsx fetchReviews function:

        dispatch({
          type: 'SET_REVIEWS_DATA',
          productId,
          data: res,
        });
      } catch (err: any) {
        console.error('[REVIEWS_ERROR]: Failed to fetch.', err);
        if (reset) {
          dispatch({ type: 'SET_REVIEWS_ERROR', productId, err });
        } else {
          ErrorNotification({
            title: 'Something went wrong...',
            message: 'Failed to load more reviews.',
          });
        }
      } finally {
        setIsFetchingMore(false);
      }
    },
    [productId, ratingFilter, sortBy, dispatch],
  );

  // 2. Action Callbacks for UI
  const loadMore = useCallback(() => {
    if (!hasMore || isFetchingMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReviews(nextPage, false);
  }, [hasMore, isFetchingMore, page, fetchReviews]);

  const applyRatingFilter = useCallback(
    (rating: number | null) => {
      setRatingFilter(rating);
      fetchReviews(1, true);
    },
    [fetchReviews],
  );

  const applySortBy = useCallback(
    (sort: SortOption) => {
      setSortBy(sort);
      fetchReviews(1, true);
    },
    [fetchReviews],
  );

  // 3. Post Review Callback
  const postReview = useCallback(
    async (data: any) => {
      if (!user) {
        ErrorNotification({
          title: 'Oops...',
          message: 'You must be logged in to leave a review.',
        });
        return { success: false };
      }

      if (!productId) return { success: false };

      try {
        const res = await ReviewAPI.post({
          ...data,
          handle,
          email: user.phone + '@dotp?.',
          name: user.fullName,
          platform: 'shopify',
          shop_domain: import.meta.env.VITE_SHOPIFY_STORE_DOMAIN.replace(
            'https://',
            '',
          ),
        });

        if (res.success) {
          SuccessNotification({
            title: 'Review Submitted!',
            message:
              'Your review has been received and is pending verification.',
          });
          return { success: true };
        }
        return { success: false };
      } catch (err: any) {
        ErrorNotification(err.message || 'Unable to post review.');
        return { success: false };
      }
    },
    [productId, user],
  );

  // 4. Inject Judge.me Scripts & Auto-fetch Initial Data
  useEffect(() => {
    // if (!document.getElementById('judgeme-config-script')) {
    //   const configScript = document.createElement('script');
    //   configScript.id = 'judgeme-config-script';
    //   configScript.innerHTML = `
    //     jdgm = window.jdgm || {};
    //     jdgm.SHOP_DOMAIN = '${import.meta.env.VITE_SHOPIFY_STORE_DOMAIN}';
    //     jdgm.PLATFORM = 'shopify';
    //     jdgm.PUBLIC_TOKEN = '${import.meta.env.VITE_JUDGEME_PUBLIC_TOKEN}';
    //   `;
    //   document.head.appendChild(configScript);
    // }

    // Only fetch if we don't already have data cached for this product
    if (productId && !productReviews?.list) {
      fetchReviews(1, true);
    }
  }, [productId, fetchReviews, productReviews?.list]);

  return {
    // Data
    data,
    rating: productReviews?.rating,
    count: productReviews?.count,
    raw: (productReviews as any)?.rawHTML,

    // States
    isLoading,
    isFetchingMore,
    hasMore,
    page,
    ratingFilter,
    sortBy,
    error: productReviews?.error || null,

    // Callbacks
    loadMore,
    applyRatingFilter,
    applySortBy,
    postReview,
    refreshReviews: () => fetchReviews(1, true),
  };
}
